// Components==============
import storeWrongAnswer from 'actions/wrongAnswer/store';
import {
    highlightChars,
    HighlightExerciseContext,
} from 'components/Exercises/HighlightExercise/HighlightExercise';
import { motion } from 'framer-motion';
import React, { useContext, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { HomeworkContext } from 'screens/Homework/Homework';
import { highlightString } from '../../utils/highlightString';
import { stripHighlightChars } from '../../utils/stripHighlightChars';
import useWrongAnimation from '../../utils/useWrongAnimation';
import './Words.module.scss';
// =========================

const variants = {
    initial: {
        opacity: 0,
    },
    whileHover: {
        opacity: 1,
    },
    wrong: {
        x: [0, -4, 4, -4, 0],
        opacity: 0,
    },
};

const Word = ({ word, wordIndex }: { word: string; wordIndex: number }) => {
    const { sequence } = useContext(HomeworkContext);
    const { setSelected, answeredSentences, selected } = useContext(
        HighlightExerciseContext
    );

    const { wrong, setWrong } = useWrongAnimation();

    const { homeworkId, treatmentId } = useParams() as {
        homeworkId: string;
        treatmentId: string;
    };

    const dispatch = useDispatch();

    const canBeHighlighted = !!highlightChars.find((char) =>
        word.includes(char)
    );
    const readableWord = stripHighlightChars(word);

    const isAnswered = (
        answeredSentences.find((as) => as.id === selected.id)?.words || []
    ).includes(wordIndex);

    function handleClick() {
        if (isAnswered) return;

        if (canBeHighlighted) {
            return setSelected((prev) => ({
                ...prev,
                wordIndex,
                type: 'word',
            }));
        }

        // prevent double click
        if (wrong) return;

        setWrong(true);
        dispatch(
            storeWrongAnswer({
                given_answer: 'verkeerd geklikt',
                correct_answer: readableWord,
                type: 'highlight',
                metadata: { sequence },
                exercise_id: homeworkId,
                exercise_type: 'homework',
                treatment_id: treatmentId,
            })
        );
    }

    const highlightedWord = highlightString(word);

    return (
        <motion.span
            initial="initial"
            animate={wrong ? 'wrong' : 'initial'}
            whileHover="whileHover"
            styleName="word"
            onClick={handleClick}
        >
            <motion.div
                styleName="block"
                variants={variants}
                transition={{ duration: 0.4 }}
            />
            {isAnswered ? (
                <span dangerouslySetInnerHTML={{ __html: highlightedWord }} />
            ) : (
                readableWord
            )}
            <wbr />
        </motion.span>
    );
};

export default function Words() {
    const { sentences, selected, setTextToBeRead } = useContext(
        HighlightExerciseContext
    );

    const sentenceSplitUpInWords = sentences[selected.index].sentence.split(
        ' '
    );

    useEffect(() => {
        setTextToBeRead(
            stripHighlightChars(sentences[selected.index].sentence)
        );
    }, []);

    return (
        <div styleName="words">
            {sentenceSplitUpInWords.map((word, i) => (
                <Word word={word} wordIndex={i} key={i} />
            ))}
        </div>
    );
}
